import React, { useState } from 'react';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import StageProgressModal from './StageProgressModal';

const StageProgress = ({ product, onUpdate, onTimelineRefresh, defaultExpanded = false }) => {
  const [isOpen, setIsOpen] = useState(defaultExpanded);
  const [showStageModal, setShowStageModal] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  const getStagesForDepartment = (department) => {
    if (department === 'product_marketing') {
      return [
        { key: 'design_concept', label: 'Design Concept', color: 'bg-indigo-500' },
        { key: 'development', label: 'Development', color: 'bg-red-500' },
        { key: 'sales_approval', label: 'Sales Approval', color: 'bg-orange-500' },
        { key: 'release', label: 'Release', color: 'bg-green-500' }
      ];
    } else if (department === 'sales') {
      return [
        { key: 'project', label: 'Project', color: 'bg-blue-500' },
        { key: 'strategy', label: 'Strategy', color: 'bg-purple-500' },
        { key: 'supply_chain', label: 'Supply Chain', color: 'bg-orange-500' },
        { key: 'delivery', label: 'Delivery', color: 'bg-green-500' }
      ];
    } else {
      // Product Development (default)
      return [
        { key: 'design_concept', label: 'Design Concept', color: 'bg-indigo-500' },
        { key: 'design_prototype', label: 'Design Prototype', color: 'bg-purple-500' },
        { key: 'development', label: 'Development', color: 'bg-red-500' },
        { key: 'testing_verification', label: 'Testing & Verification', color: 'bg-orange-500' },
        { key: 'documentation', label: 'Documentation', color: 'bg-yellow-500' },
        { key: 'release', label: 'Release', color: 'bg-green-500' }
      ];
    }
  };

  const stages = getStagesForDepartment(product.department);

  const getActiveSubstages = (stageKey) => {
    const substageMap = {
      design_concept: ['substage_1'], // Planning & Analysis only
      design_prototype: ['substage_1', 'substage_2', 'substage_3'], // All 3 stages
      development: ['substage_1', 'substage_2', 'substage_3'], // All 3 stages
      testing_verification: ['substage_3'], // Review & Testing only
      documentation: ['substage_2'], // Implementation only
      release: ['substage_2'] // Implementation only
    };
    return substageMap[stageKey] || ['substage_1', 'substage_2', 'substage_3'];
  };

  const getStageProgress = (stageKey) => {
    const stageData = product.stage_progress?.find(s => s.stage === stageKey);
    const activeSubstages = getActiveSubstages(stageKey);

    if (!stageData) return { completed: 0, total: activeSubstages.length };

    const statuses = activeSubstages.map(substageKey =>
      stageData[substageKey] || 'not_started'
    );
    const completed = statuses.filter(status => status === 'completed').length;
    return { completed, total: activeSubstages.length };
  };

  const getOverallProgress = () => {
    let totalCompleted = 0;
    let totalSubstages = 0;

    stages.forEach(stage => {
      const progress = getStageProgress(stage.key);
      totalCompleted += progress.completed;
      totalSubstages += progress.total;
    });

    return Math.round((totalCompleted / totalSubstages) * 100);
  };

  const getStageStatus = (stageKey) => {
    const progress = getStageProgress(stageKey);
    if (progress.completed === 0) return 'not-started';
    if (progress.completed === progress.total) return 'completed';
    return 'in-progress';
  };

  const handleStageClick = (stage) => {
    if (!stage || !stage.key || !stage.label) {
      console.error('Invalid stage data:', stage);
      return;
    }
    setSelectedStage(stage);
    setShowStageModal(true);
  };

  const overallProgress = getOverallProgress();

  return (
    <>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-slate-700">Overall Progress</span>
          <span className="text-sm font-medium text-slate-600">{overallProgress}%</span>
        </div>
        
        <Progress value={overallProgress} className="h-2" />

        <Collapsible open={isOpen} onOpenChange={setIsOpen}>
          <CollapsibleTrigger asChild>
            <Button 
              variant="ghost" 
              className="w-full justify-between p-2 h-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <span className="text-sm text-slate-600">
                View Stage Details
              </span>
              {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </CollapsibleTrigger>
          
          <CollapsibleContent className="space-y-2 mt-2">
            {stages.map((stage) => {
              const progress = getStageProgress(stage.key);
              const status = getStageStatus(stage.key);
              const progressPercent = Math.round((progress.completed / progress.total) * 100);

              return (
                <div
                  key={stage.key}
                  className="flex items-center justify-between p-2 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors duration-200"
                  onClick={() => handleStageClick(stage)}
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <div className={`w-3 h-3 rounded-full ${stage.color}`} />
                    <span className="text-sm font-medium text-slate-700">
                      {stage.label}
                    </span>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Badge
                      variant="outline"
                      className={`text-xs ${
                        status === 'completed' ? 'bg-green-50 text-green-700 border-green-200' :
                        status === 'in-progress' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                        'bg-slate-50 text-slate-600 border-slate-200'
                      }`}
                    >
                      {progress.completed}/{progress.total}
                    </Badge>
                    <span className="text-xs text-slate-500 w-10 text-right">
                      {progressPercent}%
                    </span>
                  </div>
                </div>
              );
            })}
          </CollapsibleContent>
        </Collapsible>
      </div>

      <StageProgressModal
        isOpen={showStageModal}
        onClose={() => setShowStageModal(false)}
        stage={selectedStage}
        product={product}
        onUpdate={onUpdate}
        onTimelineRefresh={onTimelineRefresh}
      />
    </>
  );
};

export default StageProgress;